import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp,
  limit,
  documentId
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import {
  resolveChatParticipantPair,
  canonicalPrivateLandlordUid
} from '../utils/firestoreQueries';

// Listen to messages in real-time
export const listenMessages = (chatId, callback) => {
  if (!chatId) {
    console.error('listenMessages: chatId is required');
    return () => {};
  }

  console.log('Setting up messages listener for chat:', chatId);

  // Query voor berichten in de chat, gesorteerd op createdAt
  const messagesQuery = query(
    collection(db, 'chats', chatId, 'messages'),
    orderBy('createdAt', 'asc')
  );

  // Luister naar real-time updates
  const unsubscribe = onSnapshot(
    messagesQuery,
    (snapshot) => {
      const messages = [];
      
      snapshot.forEach((doc) => {
        const messageData = doc.data();
        messages.push({
          id: doc.id,
          ...messageData,
          // Zet timestamp om naar Date object voor gebruik
          createdAt: messageData.createdAt?.toDate ? messageData.createdAt.toDate() : new Date(messageData.createdAt || 0)
        });
      });

      console.log('Messages updated for chat', chatId, ':', messages.length);
      callback(messages);
    },
    (error) => {
      console.error('Error listening to messages:', error);
      callback([]);
    }
  );

  return unsubscribe;
};

// Persistent user cache shared across all subscriptions
const _globalUserCache = new Map();

const resolveDisplayName = (data) => {
  if (!data) return null;
  return data.displayName || data.name || data.contactName ||
    (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : null) ||
    (data.firstName ? data.firstName : null) ||
    data.companyName || data.agencyProfile?.companyName ||
    data.email || null;
};

const batchFetchUsers = async (ids) => {
  const uncachedIds = ids.filter(id => id && !_globalUserCache.has(id));
  if (uncachedIds.length === 0) return;

  const chunks = [];
  for (let i = 0; i < uncachedIds.length; i += 10) {
    chunks.push(uncachedIds.slice(i, i + 10));
  }

  for (const chunk of chunks) {
    try {
      const q = query(collection(db, 'users'), where(documentId(), 'in', chunk));
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        const data = d.data();
        _globalUserCache.set(d.id, {
          uid: d.id,
          ...data,
          displayName: resolveDisplayName(data),
        });
      });
      chunk.forEach((id) => {
        if (!_globalUserCache.has(id)) {
          console.warn('[messages] User doc not found for:', id);
        }
      });
    } catch (error) {
      console.error('[messages] Batch user fetch failed:', error?.code || error?.message);
      for (const id of chunk) {
        if (_globalUserCache.has(id)) continue;
        try {
          const snap = await getDoc(doc(db, 'users', id));
          if (snap.exists()) {
            const data = snap.data();
            _globalUserCache.set(id, { uid: id, ...data, displayName: resolveDisplayName(data) });
          } else {
            console.warn('[messages] User doc not found (individual):', id);
          }
        } catch (e2) {
          console.error('[messages] Individual fetch also failed for', id, e2?.code || e2?.message);
        }
      }
    }
  }
};

// Subscribe to user chats in real-time
export const subscribeToUserChats = (userId, callback, { isAgency = false } = {}) => {
  if (!userId) {
    console.error('subscribeToUserChats: userId is required');
    return () => {};
  }

  let enrichVersion = 0;

  const enrichChats = async (chatDocs, version) => {
    const allParticipantIds = new Set();

    const rawChats = chatDocs.map((chatDoc) => {
      const chatData = typeof chatDoc.data === 'function' ? chatDoc.data() : chatDoc;
      const chatId = chatDoc.id || chatData.id;
      const participants = chatData.participants || [];

      participants.forEach(id => { if (id) allParticipantIds.add(id); });
      if (chatData.tenantId) allParticipantIds.add(chatData.tenantId);
      if (chatData.landlordId) allParticipantIds.add(chatData.landlordId);
      if (chatData.ownerId) allParticipantIds.add(chatData.ownerId);

      return { chatId, chatData, participants };
    });

    await batchFetchUsers(Array.from(allParticipantIds));

    if (version !== enrichVersion) return null;

    return rawChats.map(({ chatId, chatData, participants }) => {
      const allIds = new Set(participants);
      if (chatData.tenantId) allIds.add(chatData.tenantId);
      if (chatData.landlordId) allIds.add(chatData.landlordId);
      if (chatData.ownerId) allIds.add(chatData.ownerId);

      const participantsData = [];
      for (const pid of allIds) {
        if (!pid) continue;
        const cached = _globalUserCache.get(pid);
        participantsData.push(cached || { uid: pid, displayName: null });
      }

      const tsToDate = (v) => {
        if (!v) return null;
        if (typeof v.toDate === 'function') return v.toDate();
        if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
        if (v instanceof Date) return v;
        return new Date(v);
      };

      // Merge both read-map fields and convert timestamps to plain Dates
      const safeEntries = (obj) => {
        try {
          if (obj && typeof obj === 'object' && !obj.toDate && !obj.seconds) {
            return Object.entries(obj);
          }
        } catch (_) {}
        return [];
      };
      const mergedReadMap = {};
      safeEntries(chatData.lastReadAtBy).forEach(([uid, ts]) => {
        mergedReadMap[uid] = tsToDate(ts) || new Date(0);
      });

      return {
        id: chatId,
        ...chatData,
        participantsData,
        updatedAt: tsToDate(chatData.updatedAt) || new Date(0),
        lastMessageAt: tsToDate(chatData.lastMessageAt) || new Date(0),
        lastReadAtByUser: mergedReadMap,
        lastReadAtBy: mergedReadMap,
      };
    });
  };

  if (isAgency) {
    const q1 = query(collection(db, 'chats'), where('participants', 'array-contains', userId));
    const q2 = query(collection(db, 'chats'), where('agencyId', '==', userId));

    let rawDocs1 = new Map();
    let rawDocs2 = new Map();
    let ready1 = false;
    let ready2 = false;

    const doMergeAndEnrich = async () => {
      if (!ready1 || !ready2) return;
      enrichVersion++;
      const version = enrichVersion;

      const merged = new Map(rawDocs1);
      rawDocs2.forEach((docSnap, id) => {
        if (!merged.has(id)) merged.set(id, docSnap);
      });
      const enriched = await enrichChats(Array.from(merged.values()), version);
      if (enriched) callback(enriched);
    };

    const unsub1 = onSnapshot(q1, (snapshot) => {
      rawDocs1 = new Map();
      snapshot.docs.forEach(d => rawDocs1.set(d.id, d));
      ready1 = true;
      doMergeAndEnrich();
    }, (error) => {
      console.error('Error listening to participant chats:', error);
      ready1 = true;
      doMergeAndEnrich();
    });

    const unsub2 = onSnapshot(q2, (snapshot) => {
      rawDocs2 = new Map();
      snapshot.docs.forEach(d => rawDocs2.set(d.id, d));
      ready2 = true;
      doMergeAndEnrich();
    }, (error) => {
      console.error('Error listening to agency chats:', error);
      ready2 = true;
      doMergeAndEnrich();
    });

    return () => { unsub1(); unsub2(); };
  }

  // ── Multi-source merge: find ALL chats that belong to this user ──
  // 1. participants array-contains  (standard)
  // 2. ownerId == userId            (denormalized field)
  // 3. landlordId == userId          (denormalized field)
  // 4. chats linked to any property owned by this user (catches legacy
  //    chats where participants contained a stale UID)
  const qParticipants = query(collection(db, 'chats'), where('participants', 'array-contains', userId));
  const qOwner        = query(collection(db, 'chats'), where('ownerId', '==', userId));
  const qLandlord     = query(collection(db, 'chats'), where('landlordId', '==', userId));

  const rawSources = { participants: new Map(), owner: new Map(), landlord: new Map(), property: new Map() };
  const readyFlags  = { participants: false, owner: false, landlord: false, property: false };
  let propertyQueryUnsubs = [];

  const allReady = () => readyFlags.participants && readyFlags.owner && readyFlags.landlord && readyFlags.property;

  const mergeAndEmit = async () => {
    if (!allReady()) return;
    enrichVersion++;
    const version = enrichVersion;
    const merged = new Map();
    for (const src of Object.values(rawSources)) {
      src.forEach((docSnap, id) => { if (!merged.has(id)) merged.set(id, docSnap); });
    }
    const chats = await enrichChats(Array.from(merged.values()), version);
    if (chats) callback(chats);
  };

  const makeHandler = (key) => ({
    next: (snapshot) => {
      rawSources[key] = new Map();
      snapshot.docs.forEach((d) => rawSources[key].set(d.id, d));
      readyFlags[key] = true;
      mergeAndEmit();
    },
    error: (error) => {
      console.error(`Error listening to ${key} chats:`, error);
      readyFlags[key] = true;
      mergeAndEmit();
    }
  });

  const hP = makeHandler('participants');
  const hO = makeHandler('owner');
  const hL = makeHandler('landlord');
  const unsubP = onSnapshot(qParticipants, hP.next, hP.error);
  const unsubO = onSnapshot(qOwner, hO.next, hO.error);
  const unsubL = onSnapshot(qLandlord, hL.next, hL.error);

  // 4. Property-based: find all property IDs owned by this user, then listen
  //    for chats linked to those properties.
  const propertyFieldVariants = ['verhuurderID', 'landlordId', 'ownerId'];
  const setupPropertyChatListeners = async () => {
    try {
      const myPropertyIds = new Set();
      for (const field of propertyFieldVariants) {
        try {
          const snap = await getDocs(query(collection(db, 'koten'), where(field, '==', userId)));
          snap.docs.forEach((d) => myPropertyIds.add(d.id));
        } catch (_) { /* index may not exist for every variant */ }
      }

      if (myPropertyIds.size === 0) {
        readyFlags.property = true;
        mergeAndEmit();
        return;
      }

      const idArray = Array.from(myPropertyIds);
      const chatFieldVariants = ['propertyId', 'kotID', 'listingId'];
      let remaining = 0;

      for (let i = 0; i < idArray.length; i += 10) {
        const chunk = idArray.slice(i, i + 10);
        for (const chatField of chatFieldVariants) {
          remaining++;
          const q = query(collection(db, 'chats'), where(chatField, 'in', chunk));
          const unsub = onSnapshot(q, (snap) => {
            snap.docs.forEach((d) => rawSources.property.set(d.id, d));
            remaining--;
            if (remaining <= 0) { readyFlags.property = true; mergeAndEmit(); }
          }, () => {
            remaining--;
            if (remaining <= 0) { readyFlags.property = true; mergeAndEmit(); }
          });
          propertyQueryUnsubs.push(unsub);
        }
      }

      if (remaining === 0) {
        readyFlags.property = true;
        mergeAndEmit();
      }
    } catch (err) {
      console.error('Error setting up property-based chat listeners:', err);
      readyFlags.property = true;
      mergeAndEmit();
    }
  };

  setupPropertyChatListeners();

  return () => {
    unsubP();
    unsubO();
    unsubL();
    propertyQueryUnsubs.forEach((u) => u());
  };
};

// Create or get existing chat between two users
export const createOrGetChat = async (user1Id, user2Id, propertyId = null) => {
  try {
    console.log('=== CREATE OR GET CHAT ===');
    console.log('User1 ID:', user1Id);
    console.log('User2 ID:', user2Id);
    console.log('Property ID:', propertyId);

    let propertyData = null;
    if (propertyId) {
      try {
        const propertyDoc = await getDoc(doc(db, 'koten', propertyId));
        if (propertyDoc.exists()) {
          propertyData = { id: propertyDoc.id, ...propertyDoc.data() };
        }
      } catch (error) {
        console.warn('Chat metadata lookup issue:', error?.message || error);
        throw error;
      }
    }

    const pair = resolveChatParticipantPair(propertyData, user1Id, user2Id);

    const matchesExistingChat = (chatData) => {
      const p = chatData.participants || [];
      if (p.length !== 2) return false;
      if (!p.includes(pair.tenantUid)) return false;
      if (p.includes(pair.landlordUid)) {
        if (propertyId) return chatData.propertyId === propertyId;
        return true;
      }
      if (
        pair.landlordUid &&
        (chatData.ownerId === pair.landlordUid || chatData.landlordId === pair.landlordUid)
      ) {
        if (propertyId) return chatData.propertyId === propertyId;
        return true;
      }
      if (
        propertyId &&
        chatData.propertyId === propertyId &&
        p.includes(pair.tenantUid) &&
        (p.includes(user1Id) || p.includes(user2Id))
      ) {
        return true;
      }
      return false;
    };

    const currentUid = auth.currentUser?.uid;
    const queryUid = currentUid && (currentUid === user1Id || currentUid === user2Id)
      ? currentUid
      : user1Id;

    console.log('Searching for existing chats...');
    const existingChatQuery = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', queryUid)
    );

    const existingChats = await getDocs(existingChatQuery);
    console.log('Found existing chats:', existingChats.docs.length);

    for (const chatDoc of existingChats.docs) {
      const chatData = chatDoc.data();
      console.log('Checking chat:', chatDoc.id, 'participants:', chatData.participants);

      if (!matchesExistingChat(chatData)) continue;

      const p = chatData.participants || [];
      const needsParticipantFix =
        pair.landlordUid &&
        !p.includes(pair.landlordUid) &&
        currentUid === pair.tenantUid &&
        p.includes(pair.tenantUid);

      if (needsParticipantFix) {
        try {
          await updateDoc(doc(db, 'chats', chatDoc.id), {
            participants: pair.participants,
            landlordId: pair.landlordUid,
            tenantId: pair.tenantUid,
            ownerId: canonicalPrivateLandlordUid(propertyData) || pair.landlordUid,
            updatedAt: new Date()
          });
          chatData.participants = pair.participants;
          chatData.landlordId = pair.landlordUid;
          chatData.tenantId = pair.tenantUid;
          chatData.ownerId = canonicalPrivateLandlordUid(propertyData) || pair.landlordUid;
        } catch (e) {
          console.warn('Could not migrate chat participants:', e?.message || e);
        }
      }

      console.log('✅ Found existing chat:', chatDoc.id);
      return {
        id: chatDoc.id,
        ...chatData,
        exists: true
      };
    }

    // If no existing chat found, create new one
    console.log('No existing chat found, creating new one...');
    const agencyId = propertyData?.management?.agencyId || propertyData?.createdByAgencyId || null;
    const clientLinkId = propertyData?.management?.clientLinkId || null;
    const ownerId =
      canonicalPrivateLandlordUid(propertyData) || pair.landlordUid || null;
    const now = new Date();

    const newChatData = {
      participants: pair.participants,
      createdAt: now,
      updatedAt: now,
      lastMessage: '',
      lastMessageAt: now,
      propertyId: propertyId || null,
      landlordId: pair.landlordUid,
      tenantId: pair.tenantUid,
      ownerId,
      agencyId,
      clientLinkId
    };
    
    console.log('Creating new chat with data:', newChatData);
    const chatRef = await addDoc(collection(db, 'chats'), newChatData);
    console.log('✅ Created new chat:', chatRef.id);
    console.log('Chat ref path:', chatRef.path);
    
    // Verify the chat was created
    try {
      const verifyDoc = await getDoc(doc(db, 'chats', chatRef.id));
      console.log('Verification - Document exists:', verifyDoc.exists());
      
      if (verifyDoc.exists()) {
        const verifiedData = verifyDoc.data();
        console.log('✅ Chat verification successful');
        console.log('Verified chat data:', verifiedData);
        
        const result = { 
          id: chatRef.id, 
          ...verifiedData,
          exists: false // New chat, not existing
        };
        console.log('Returning chat result:', result);
        return result;
      } else {
        console.log('❌ Chat verification failed - document does not exist');
        throw new Error('Chat creation verification failed - document not found');
      }
    } catch (verifyError) {
      console.error('❌ Error verifying chat:', verifyError);
      // Still return the chat with the ID from addDoc
      const result = { 
        id: chatRef.id, 
        ...newChatData,
        exists: false
      };
      console.log('Returning chat result (without verification):', result);
      return result;
    }
  } catch (error) {
    console.error('❌ Error creating or getting chat:', error);
    throw error;
  }
};

// Send a message
export const sendMessage = async (chatId, senderId, messageText, messageType = 'text') => {
  try {
    console.log('Sending message to chat:', chatId, 'from:', senderId);
    
    const now = new Date();
    const messageData = {
      senderId,
      text: messageText,
      type: messageType,
      createdAt: now,
      read: false
    };
    
    // Store message in the chat's subcollection
    const messageRef = await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
    console.log('Message sent:', messageRef.id);
    
    // Update chat with last message info
    await updateDoc(doc(db, 'chats', chatId), {
      lastMessage: messageText,
      lastMessageAt: now,
      lastSenderId: senderId,
      [`lastReadAtBy.${senderId}`]: now,
      updatedAt: now
    });
    console.log('Chat updated with last message');
    
    return { id: messageRef.id, ...messageData };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Get messages for a chat
export const getMessages = async (chatId, limitCount = 50) => {
  try {
    console.log('Getting messages for chat:', chatId);
    
    const messagesQuery = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(limitCount)
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    const messages = messagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || 0)
    }));
    
    console.log('Found messages:', messages.length);
    return messages; // Already in correct order (oldest first)
  } catch (error) {
    console.error('Error getting messages:', error);
    // Return empty array instead of throwing error
    return [];
  }
};

// Get chats for a user - simplified version
export const getUserChats = async (userId) => {
  try {
    console.log('Getting chats for user:', userId);
    
    if (!userId) {
      console.log('No userId provided');
      return [];
    }
    
    const qParticipants = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId)
    );
    const qOwner = query(
      collection(db, 'chats'),
      where('ownerId', '==', userId)
    );

    const [partSnap, ownerSnap] = await Promise.all([
      getDocs(qParticipants),
      getDocs(qOwner)
    ]);

    const mergedById = new Map();
    partSnap.docs.forEach((d) => mergedById.set(d.id, d));
    ownerSnap.docs.forEach((d) => mergedById.set(d.id, d));

    console.log('Found chats:', mergedById.size);

    const chats = [];

    for (const chatDoc of mergedById.values()) {
      const chatData = chatDoc.data();
      const otherParticipantId =
        (chatData.tenantId && chatData.tenantId !== userId && chatData.tenantId) ||
        chatData.participants?.find((id) => id !== userId);
      
      if (otherParticipantId) {
        try {
          // Get other participant's info
          const otherUserDoc = await getDoc(doc(db, 'users', otherParticipantId));
          const otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : null;
          
          chats.push({
            id: chatDoc.id,
            ...chatData,
            otherParticipant: {
              id: otherParticipantId,
              ...otherUserData
            }
          });
        } catch (userError) {
          console.error('Error getting user data for:', otherParticipantId, userError);
          // Still add the chat even if user data fails
          chats.push({
            id: chatDoc.id,
            ...chatData,
            otherParticipant: {
              id: otherParticipantId,
              displayName: 'Onbekende gebruiker'
            }
          });
        }
      }
    }
    
    console.log('Processed chats:', chats.length);
    return chats;
  } catch (error) {
    console.error('Error getting user chats:', error);
    // Return empty array instead of throwing error
    return [];
  }
};

// Mark messages as read
export const markMessagesAsRead = async (chatId, userId) => {
  try {
    // 1) Mark individual messages as read
    const messagesSnapshot = await getDocs(
      query(collection(db, 'chats', chatId, 'messages'))
    );
    
    const unreadMessages = messagesSnapshot.docs.filter(msgDoc => {
      const data = msgDoc.data();
      return data.senderId !== userId && data.read !== true;
    });
    
    if (unreadMessages.length > 0) {
      await Promise.all(unreadMessages.map(msgDoc => updateDoc(msgDoc.ref, { read: true })));
    }

    // 2) Set lastReadAtBy to current time
    await updateDoc(doc(db, 'chats', chatId), {
      [`lastReadAtBy.${userId}`]: new Date(),
    });
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

// Subscribe to messages for real-time updates
export const subscribeToMessages = (chatId, callback) => {
  try {
    console.log('Subscribing to messages for chat:', chatId);
    
    const messagesQuery = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    
    return onSnapshot(messagesQuery, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || 0)
      }));
      console.log('Messages updated:', messages.length);
      callback(messages);
    }, (error) => {
      console.error('Error in messages subscription:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up messages subscription:', error);
    // Return a function that does nothing
    return () => {};
  }
};


// Get unread message count for a user
export const getUnreadCount = async (userId) => {
  try {
    console.log('Getting unread count for user:', userId);
    
    if (!userId) {
      return 0;
    }
    
    // Get all chats for user
    const chats = await getUserChats(userId);
    let totalUnread = 0;
    
    for (const chat of chats) {
      try {
        // Get all messages and filter client-side to avoid index requirement
        const messagesQuery = query(
          collection(db, 'chats', chat.id, 'messages')
        );
        
        const messagesSnapshot = await getDocs(messagesQuery);
        
        // Filter client-side: messages not from current user and not read
        const unreadCount = messagesSnapshot.docs.filter(doc => {
          const data = doc.data();
          return data.senderId !== userId && data.read !== true;
        }).length;
        
        totalUnread += unreadCount;
      } catch (error) {
        console.error('Error getting unread count for chat:', chat.id, error);
        // Continue with other chats
      }
    }
    
    console.log('Total unread messages:', totalUnread);
    return totalUnread;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};